import React, { useState, useEffect, useCallback } from 'react';
import { Box, Typography, Button, IconButton } from '@mui/material';
import ChevronLeftIcon from '@mui/icons-material/ChevronLeft';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';

export interface PromoSlide {
  id: number;
  headline: string;
  subtext: string;
  cta: string;
  /** 'scroll' = scroll to #category-grid, 'filter' = apply a search query, 'link' = open URL */
  action: 'scroll' | 'filter' | 'link';
  actionValue?: string; // query string for 'filter', URL for 'link'
  image: string;
}

const SLIDES: PromoSlide[] = [
  {
    id: 1,
    headline: 'Real enterprise prices. No quote forms. No sales calls.',
    subtext: 'Browse and buy connectivity, cloud, voice, and AI services — see actual prices upfront.',
    cta: 'Explore services',
    action: 'scroll',
    image: '/assets/marketplace/promo/slide1.jpg',
  },
  {
    id: 2,
    headline: 'AI is reshaping how businesses buy technology.',
    subtext: '73% of enterprise buyers say AI-assisted procurement cuts vendor evaluation time in half.',
    cta: 'See how it works',
    action: 'link',
    actionValue: '/how-it-works',
    image: '/assets/marketplace/promo/slide2.jpg',
  },
  {
    id: 3,
    headline: 'Spectrum Enterprise is now available online.',
    subtext: 'Dedicated fiber, SD-WAN, and UCaaS — quoted and ordered in minutes, not weeks.',
    cta: 'View Spectrum services',
    action: 'filter',
    actionValue: 'Spectrum',
    image: '/assets/marketplace/promo/slide3.jpg',
  },
  {
    id: 4,
    headline: "Here's what happens after you place an order.",
    subtext: "Address verified → service agreement sent → install scheduled → you're live. No calls required.",
    cta: 'See the full process',
    action: 'link',
    actionValue: '/how-it-works',
    image: '/assets/marketplace/promo/slide4.jpg',
  },
];

const INTERVAL_MS = 7000;

interface PromoBarProps {
  accentColor?: string;
  /** Called with the search string when action='filter' */
  onFilter?: (query: string) => void;
}

export const PromoBar: React.FC<PromoBarProps> = ({
  accentColor = '#1976d2',
  onFilter,
}) => {
  const [active, setActive] = useState(0);
  const [paused, setPaused] = useState(false);

  const next = useCallback(() => setActive((a) => (a + 1) % SLIDES.length), []);
  const prev = useCallback(() => setActive((a) => (a - 1 + SLIDES.length) % SLIDES.length), []);

  useEffect(() => {
    if (paused) return;
    const t = setInterval(next, INTERVAL_MS);
    return () => clearInterval(t);
  }, [paused, next]);

  const handleCta = (slide: PromoSlide) => {
    if (slide.action === 'scroll') {
      const el = document.getElementById('category-grid');
      if (el) el.scrollIntoView({ behavior: 'smooth', block: 'start' });
    } else if (slide.action === 'filter' && slide.actionValue) {
      onFilter?.(slide.actionValue);
    } else if (slide.action === 'link' && slide.actionValue) {
      window.open(slide.actionValue, '_blank', 'noopener');
    }
  };

  const slide = SLIDES[active];

  return (
    <Box
      onMouseEnter={() => setPaused(true)}
      onMouseLeave={() => setPaused(false)}
      sx={{
        position: 'relative',
        width: '100%',
        height: { xs: 180, sm: 220, md: 260 },
        overflow: 'hidden',
        borderRadius: 2,
        mb: 3,
        cursor: 'default',
      }}
    >
      {/* Background images with crossfade */}
      {SLIDES.map((s, i) => (
        <Box
          key={s.id}
          component="img"
          src={s.image}
          alt=""
          aria-hidden
          sx={{
            position: 'absolute',
            inset: 0,
            width: '100%',
            height: '100%',
            objectFit: 'cover',
            objectPosition: 'center',
            opacity: i === active ? 1 : 0,
            transition: 'opacity 0.7s ease',
          }}
        />
      ))}

      {/* Dark gradient overlay — left-heavy for text legibility */}
      <Box
        sx={{
          position: 'absolute',
          inset: 0,
          background:
            'linear-gradient(90deg, rgba(0,0,0,0.72) 0%, rgba(0,0,0,0.38) 60%, rgba(0,0,0,0.10) 100%)',
        }}
      />

      {/* Text content */}
      <Box
        sx={{
          position: 'absolute',
          inset: 0,
          display: 'flex',
          flexDirection: 'column',
          justifyContent: 'center',
          px: { xs: 2.5, sm: 4, md: 5 },
          maxWidth: { xs: '100%', md: '62%' },
        }}
      >
        <Typography
          variant="h5"
          sx={{
            color: '#fff',
            fontWeight: 800,
            fontSize: { xs: '1rem', sm: '1.2rem', md: '1.45rem' },
            lineHeight: 1.25,
            mb: 0.75,
            textShadow: '0 1px 4px rgba(0,0,0,0.5)',
          }}
        >
          {slide.headline}
        </Typography>
        <Typography
          sx={{
            color: 'rgba(255,255,255,0.88)',
            fontSize: { xs: '0.72rem', sm: '0.82rem', md: '0.9rem' },
            mb: 1.5,
            lineHeight: 1.4,
            textShadow: '0 1px 3px rgba(0,0,0,0.5)',
          }}
        >
          {slide.subtext}
        </Typography>
        <Box>
          <Button
            size="small"
            variant="contained"
            onClick={() => handleCta(slide)}
            sx={{
              bgcolor: accentColor,
              color: '#fff',
              fontWeight: 700,
              fontSize: '0.78rem',
              px: 2,
              py: 0.6,
              borderRadius: 1.5,
              textTransform: 'none',
              '&:hover': { bgcolor: accentColor, filter: 'brightness(1.12)' },
            }}
          >
            {slide.cta} →
          </Button>
        </Box>
      </Box>

      {/* Prev / Next arrows */}
      <IconButton
        onClick={prev}
        size="small"
        sx={{
          position: 'absolute',
          left: 8,
          top: '50%',
          transform: 'translateY(-50%)',
          bgcolor: 'rgba(0,0,0,0.35)',
          color: '#fff',
          '&:hover': { bgcolor: 'rgba(0,0,0,0.6)' },
        }}
      >
        <ChevronLeftIcon fontSize="small" />
      </IconButton>
      <IconButton
        onClick={next}
        size="small"
        sx={{
          position: 'absolute',
          right: 8,
          top: '50%',
          transform: 'translateY(-50%)',
          bgcolor: 'rgba(0,0,0,0.35)',
          color: '#fff',
          '&:hover': { bgcolor: 'rgba(0,0,0,0.6)' },
        }}
      >
        <ChevronRightIcon fontSize="small" />
      </IconButton>

      {/* Dot indicators */}
      <Box
        sx={{
          position: 'absolute',
          bottom: 10,
          left: 0,
          right: 0,
          display: 'flex',
          justifyContent: 'center',
          gap: 0.75,
        }}
      >
        {SLIDES.map((_, i) => (
          <Box
            key={i}
            onClick={() => setActive(i)}
            sx={{
              width: i === active ? 20 : 8,
              height: 8,
              borderRadius: 4,
              bgcolor: i === active ? accentColor : 'rgba(255,255,255,0.5)',
              cursor: 'pointer',
              transition: 'width 0.3s ease, background-color 0.3s ease',
            }}
          />
        ))}
      </Box>
    </Box>
  );
};

export default PromoBar;
